/**
 * Intro Request update - pure state transition logic.
 * 
 * Elm-ish architecture: This function is pure.
 * - No API calls
 * - No storage access
 * - No hooks
 * - No side effects
 * - No mutation
 * 
 * Each message produces a new model and any effects that need to run.
 */
import type { IntroRequestModel } from './model';
import type { IntroRequestMsg } from './messages';
import { validateForm } from './model';

/**
 * Effect types that can be triggered by state transitions.
 * These are handled by the effect runner (useIntroRequestProgram.ts).
 */
export type IntroRequestEffect =
  /** Load card data by ID */
  | { readonly type: 'LoadCard'; readonly cardId: string }
  /** Check if intro request already exists for this card */
  | { readonly type: 'CheckDuplicate'; readonly cardId: string }
  /** Submit the intro request */
  | { readonly type: 'SubmitRequest'; readonly cardId: string; readonly message: string };

/**
 * Result of an update call - new model plus effects to run.
 */
export interface UpdateResult {
  readonly model: IntroRequestModel;
  readonly effects: readonly IntroRequestEffect[];
}

/**
 * Pure update function - the heart of the Elm-ish architecture.
 * 
 * @param model - Current state
 * @param msg - Message triggering the transition
 * @returns New state and effects to run
 */
export function update(
  model: IntroRequestModel,
  msg: IntroRequestMsg
): UpdateResult {
  switch (msg.type) {
    case 'Started':
      if (!msg.cardId) {
        // No card ID provided
        return {
          model: {
            ...model,
            status: 'no_card',
            cardId: null,
            card: null,
          },
          effects: [],
        };
      }
      // Start loading card data
      return {
        model: {
          ...model,
          status: 'loading',
          cardId: msg.cardId,
          error: null,
        },
        effects: [{ type: 'LoadCard', cardId: msg.cardId }],
      };

    case 'CardLoadFailed':
      // Card not found or load error
      return {
        model: {
          ...model,
          status: 'no_card',
          card: null,
          error: null, // No user-visible error for missing card
        },
        effects: [],
      };

    case 'CardLoaded':
      if (!msg.card) {
        // Card not found
        return {
          model: {
            ...model,
            status: 'no_card',
            card: null,
          },
          effects: [],
        };
      }
      // Card loaded successfully, now check for duplicate
      return {
        model: {
          ...model,
          card: msg.card,
        },
        effects: [{ type: 'CheckDuplicate', cardId: msg.card.id }],
      };

    case 'DuplicateDetected':
      return {
        model: {
          ...model,
          status: 'duplicate',
          duplicateRequestId: msg.requestId,
        },
        effects: [],
      };

    case 'NoDuplicate':
      return {
        model: {
          ...model,
          status: 'form',
        },
        effects: [],
      };

    case 'MessageEdited':
      return {
        model: {
          ...model,
          message: msg.message,
          validationErrors: validateForm(msg.message, model.acknowledgementChecked),
        },
        effects: [],
      };

    case 'AcknowledgementToggled':
      return {
        model: {
          ...model,
          acknowledgementChecked: msg.checked,
          validationErrors: validateForm(model.message, msg.checked),
        },
        effects: [],
      };

    case 'SubmitClicked': {
      const errors = validateForm(model.message, model.acknowledgementChecked);
      if (errors.length > 0) {
        // Invalid form - show errors but don't submit
        return {
          model: {
            ...model,
            validationErrors: errors,
          },
          effects: [],
        };
      }
      
      // Valid form - transition to submitting
      return {
        model: {
          ...model,
          status: 'submitting',
          validationErrors: [],
        },
        effects: [{ type: 'SubmitRequest', cardId: model.cardId!, message: model.message }],
      };
    }

    case 'SubmitSucceeded':
      return {
        model: {
          ...model,
          status: 'success',
          requestId: msg.requestId,
        },
        effects: [],
      };

    case 'SubmitFailed':
      return {
        model: {
          ...model,
          status: 'error',
          error: msg.error,
        },
        effects: [],
      };

    case 'RetryClicked':
      return {
        model: {
          ...model,
          status: 'form',
          error: null,
        },
        effects: [],
      };

    case 'BackClicked':
      return {
        model: {
          ...model,
          status: 'form',
          error: null,
        },
        effects: [],
      };

    default:
      // Exhaustive check - should never reach here
      const _exhaustive: never = msg;
      return { model, effects: [] };
  }
}